import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Loader2, BadgePercent, CreditCard, FileText, RefreshCw, Sparkles, Eye, ChevronLeft, ChevronRight, Banknote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plantillas } from "@/components/Plantillas";

const CAMPANAS = [
  { key: "promociones", titulo: "Promociones disponibles", icono: BadgePercent,
    texto: "Avisa a cada familia de los planes segmentados para los que cumple requisitos. Automático los lunes." },
  { key: "pagos-pendientes", titulo: "Cuotas pendientes", icono: CreditCard,
    texto: "Recordatorio a las familias con la cuota pendiente o impagada. Automático cada día a las 9:00." },
  { key: "evaluaciones", titulo: "Evaluación mensual", icono: Sparkles,
    texto: "Trabajos del mes, asistencia y cómo llegó de ánimo cada niño al taller. Automático el día 1 de cada mes." },
  { key: "facturas", titulo: "Facturas", icono: FileText,
    texto: "Emite las facturas pendientes de los pagos cobrados y las envía a la familia. Automático cada día." },
  { key: "renovaciones", titulo: "Renovaciones anuales", icono: RefreshCw,
    texto: "Avisa a la familia (con copia al franquiciado) un mes antes de que caduque la matrícula o el seguro. Automático cada día." },
  { key: "cobros-manuales", titulo: "Cobros manuales por vencer", icono: Banknote,
    texto: "Resumen interno por sede de los alumnos que pagaron en efectivo o transferencia y vencen en 7 días o hoy. Automático cada día a las 8:30." },
];

export default function Comunicaciones() {
  const [sedes, setSedes] = useState([]);
  const [sede, setSede] = useState("todas");
  const [resumen, setResumen] = useState(null);
  const [log, setLog] = useState([]);
  const [enviando, setEnviando] = useState(null);
  const [mes, setMes] = useState("");
  const [envio, setEnvio] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [meta, setMeta] = useState({ total: 0, paginas: 1 });

  const verEnvio = async (id) => {
    const { data } = await api.get(`/notificaciones/log/${id}`);
    setEnvio(data);
  };

  const load = () => {
    api.get("/notificaciones/resumen").then(({ data }) => setResumen(data));
    api.get("/notificaciones/log", { params: { pagina, por_pagina: 10 } }).then(({ data }) => {
      setLog(data.envios);
      setMeta({ total: data.total, paginas: data.paginas });
    });
  };
  useEffect(() => {
    api.get("/sedes").then(({ data }) => setSedes(data));
  }, []);
  useEffect(() => { load(); }, [pagina]);

  const [previo, setPrevio] = useState(null); // { key, titulo, total, destinatarios }

  const revisar = async (key, titulo) => {
    setEnviando(key);
    try {
      const params = { campana: key };
      if (key !== "facturas" && sede !== "todas") params.sede_id = sede;
      const { data } = await api.get("/notificaciones/destinatarios", { params });
      setPrevio({ key, titulo, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEnviando(null);
    }
  };

  const enviar = async (key) => {
    setEnviando(key);
    try {
      const body = key === "facturas" ? {} : { sede_id: sede === "todas" ? null : sede };
      if (key === "evaluaciones" && mes) body.mes = mes;
      const { data } = await api.post(`/notificaciones/${key}`, body);
      toast.success(`Emails enviados: ${data.enviados ?? 0}`);
      setPrevio(null);
      setPagina(1);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setEnviando(null);
    }
  };

  return (
    <div data-testid="comunicaciones-page">
      <PageHeader
        title="Comunicaciones"
        subtitle="Avisos por email a las familias. Los envíos programados funcionan solos; aquí puedes lanzarlos a mano."
        action={
          <Select value={sede} onValueChange={setSede}>
            <SelectTrigger className="w-56 h-11 rounded-lg" data-testid="comunicaciones-sede-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas mis sedes</SelectItem>
              {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-9">
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-pagos-pendientes">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Cuotas pendientes</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.pagos_pendientes ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-promos">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Planes promocionales</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.planes_promocionales ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-facturas">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Facturas sin enviar</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.facturas_sin_enviar ?? 0}</p>
        </Card>
        <Card className="rounded-2xl p-5 border-border shadow-sm" data-testid="metric-envios">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Envíos (7 días)</p>
          <p className="font-display text-3xl font-extrabold mt-3">{resumen?.envios_ultimos_7_dias ?? 0}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {CAMPANAS.map(({ key, titulo, texto, icono: Icono }) => (
          <Card key={key} className="rounded-2xl p-6 border-border shadow-sm" data-testid={`campana-${key}`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center">
                <Icono className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display font-bold text-lg leading-tight">{titulo}</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-4">{texto}</p>
            {key === "evaluaciones" && (
              <div className="mt-5">
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Mes a enviar
                </label>
                <Input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
                  className="mt-2 h-10 rounded-lg" data-testid="evaluaciones-mes-input" />
                <p className="text-xs text-muted-foreground mt-2">Si lo dejas vacío se envía el mes anterior.</p>
              </div>
            )}
            <Button className="rounded-full font-semibold mt-6" disabled={enviando === key}
              onClick={() => revisar(key, titulo)} data-testid={`enviar-${key}-btn`}>
              {enviando === key ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Send className="h-4 w-4 mr-2" /> Revisar y enviar</>}
            </Button>
          </Card>
        ))}
      </div>

      <Dialog open={!!previo} onOpenChange={(v) => !v && setPrevio(null)}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="destinatarios-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{previo?.titulo}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="destinatarios-total">
            Se enviará a <b className="text-foreground">{previo?.total ?? 0}</b> destinatario(s):
          </p>
          <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border">
            {(previo?.destinatarios || []).map((d) => (
              <div key={d.email} className="px-4 py-2.5 text-sm" data-testid={`destinatario-${d.email}`}>
                <p className="font-semibold">{d.nombre || d.email}</p>
                <p className="text-xs text-muted-foreground">{d.email} · {d.motivo}</p>
              </div>
            ))}
            {(previo?.total ?? 0) === 0 && (
              <p className="px-4 py-4 text-sm text-muted-foreground" data-testid="destinatarios-vacio">
                Ahora mismo no hay nadie a quien enviar esta comunicación.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" disabled={!previo?.total || enviando === previo?.key}
              onClick={() => enviar(previo.key)} data-testid="confirmar-envio-btn">
              {enviando === previo?.key ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Send className="h-4 w-4 mr-2" /> Enviar ahora</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <h2 className="font-display text-2xl font-extrabold mt-12 mb-5">Últimos envíos</h2>
      <Card className="rounded-2xl border-border shadow-sm divide-y divide-border" data-testid="log-envios">
        {log.slice(0, 10).map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-4 px-5 py-3.5 text-sm">
            <div className="min-w-0">
              <p className="font-semibold capitalize">
                {l.tipo.replace("_", " ")}{l.matricula ? ` · ${l.matricula}` : ""}
              </p>
              <p className="text-muted-foreground text-xs truncate">
                {l.padre ? `${l.padre} · ` : ""}{l.asunto ? `${l.asunto} · ` : ""}{l.destinatario} · {(l.fecha || "").slice(0, 16).replace("T", " ")}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => verEnvio(l.id)}
                data-testid={`ver-envio-${l.id}`}>
                <Eye className="h-4 w-4" />
              </Button>
              <Badge variant={l.ok ? "default" : "destructive"}>{l.ok ? "Enviado" : "Error"}</Badge>
            </div>
          </div>
        ))}
        {log.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">Todavía no se ha enviado ningún email.</p>}
      </Card>

      {meta.total > 0 && (
        <div className="flex items-center justify-between gap-4 mt-4" data-testid="log-paginacion">
          <p className="text-xs text-muted-foreground">
            Página {pagina} de {meta.paginas} · {meta.total} envíos en total
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={pagina <= 1}
              onClick={() => setPagina((p) => p - 1)} data-testid="log-anteriores-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Más recientes
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={pagina >= meta.paginas}
              onClick={() => setPagina((p) => p + 1)} data-testid="log-siguientes-btn">
              Ver anteriores <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Plantillas />

      <Dialog open={!!envio} onOpenChange={(v) => !v && setEnvio(null)}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="envio-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{envio?.asunto || "Email enviado"}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            {envio?.destinatario} · {(envio?.fecha || "").slice(0, 16).replace("T", " ")}
            {envio?.ok ? "" : ` · Error: ${envio?.detalle}`}
          </p>
          <iframe title="Email enviado" srcDoc={envio?.html || "<p>Este envío no guardó el contenido.</p>"}
            className="w-full h-[60vh] rounded-lg border border-border bg-white" data-testid="envio-iframe" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
